import { useEffect, useState } from 'react';

import { Alert, Box, Button, Code, Divider, Stack, Text } from '@mantine/core';
import { isHTTPError } from 'ky';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

import { API_URL } from '@/utils/environment';

interface Props {
  readonly error: unknown;
  readonly reset?: () => void;
}

type HealthStatus = 'checking' | 'up' | 'down';

export function RouteError({ error, reset }: Props) {
  const [responseBody, setResponseBody] = useState<string | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('checking');

  const err = error instanceof Error ? error : new Error(String(error));
  const httpError = isHTTPError(err) ? err : null;
  const request = httpError?.request;
  const response = httpError?.response;

  useEffect(() => {
    if (!response) return;
    response
      .clone()
      .text()
      .then((t) => {
        setResponseBody(t || null);
      })
      .catch(() => null);
  }, [response]);

  useEffect(() => {
    fetch(`${API_URL}/api/info`, { credentials: 'include' })
      .then((r) => setHealthStatus(r.ok ? 'up' : 'down'))
      .catch(() => setHealthStatus('down'));
  }, []);

  return (
    <Box p='xl' maw={800} mx='auto' mt='xl'>
      <Alert icon={<AlertTriangle size={18} />} color='brown' title='Something went wrong' mb='lg'>
        {err.message || 'An unexpected error occurred.'}
      </Alert>

      <Stack gap='sm'>
        <Box>
          <Text size='xs' fw={600} tt='uppercase' c='charcoal.4' mb={4}>
            Backend status
          </Text>
          {healthStatus === 'checking' && (
            <Text size='sm' c='charcoal.5'>
              Checking…
            </Text>
          )}
          {healthStatus === 'up' && (
            <Text size='sm' c='teal.6' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} /> Backend reachable
            </Text>
          )}
          {healthStatus === 'down' && (
            <Text size='sm' c='brown.6' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <XCircle size={14} /> Backend unreachable
            </Text>
          )}
        </Box>

        {request && (
          <Box>
            <Text size='xs' fw={600} tt='uppercase' c='charcoal.4' mb={4}>
              Request
            </Text>
            <Code block>
              {request.method} {request.url}
            </Code>
          </Box>
        )}

        {response && (
          <Box>
            <Text size='xs' fw={600} tt='uppercase' c='charcoal.4' mb={4}>
              HTTP Status
            </Text>
            <Code block>
              {response.status} {response.statusText}
            </Code>
          </Box>
        )}

        {responseBody && (
          <Box>
            <Text size='xs' fw={600} tt='uppercase' c='charcoal.4' mb={4}>
              Server response
            </Text>
            <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {responseBody}
            </Code>
          </Box>
        )}

        {err.stack && (
          <Box>
            <Text size='xs' fw={600} tt='uppercase' c='charcoal.4' mb={4}>
              Stack trace
            </Text>
            <Code block style={{ whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>
              {err.stack}
            </Code>
          </Box>
        )}

        {reset && (
          <>
            <Divider />
            <Button variant='subtle' color='charcoal' onClick={reset} size='sm' w='fit-content'>
              Retry
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
